(() => {
  const namespace = window.LumiNovaPillowStory || {};
  window.LumiNovaPillowStory = namespace;

  const PAGE_VERSION = 'A-v1.2';
  const EVENT_NAME = 'luminova:pillow-story';

  const deviceType = () => window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionBehavior = () => motionPreference.matches ? 'auto' : 'smooth';

  const emit = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(EVENT_NAME, {
      detail: {
        event: name,
        landingPageVersion: PAGE_VERSION,
        device: deviceType(),
        viewportWidth: window.innerWidth,
        ...detail
      }
    }));
  };

  class PillowStoryController {
    constructor() {
      this.abortController = new AbortController();
      this.reachObserver = null;
      this.heroObserver = null;
      this.commerceObserver = null;
      this.footerObserver = null;
      this.reachedSections = new WeakSet();
      this.commerceReached = false;
      this.heroExited = false;
      this.cartLocked = false;
      this.refreshTimer = null;

      const listenerOptions = { signal: this.abortController.signal };
      document.addEventListener('click', (event) => this.onClick(event), listenerOptions);
      document.addEventListener('input', (event) => this.onInput(event), listenerOptions);
      document.addEventListener('keydown', (event) => this.onKeydown(event), listenerOptions);
      document.addEventListener('submit', (event) => this.onSubmit(event), listenerOptions);
      document.addEventListener('toggle', (event) => this.onToggle(event), {
        capture: true,
        signal: this.abortController.signal
      });
      document.addEventListener('shopify:section:load', () => this.scheduleRefresh(), listenerOptions);
      document.addEventListener('shopify:section:unload', () => this.scheduleRefresh(), listenerOptions);
      motionPreference.addEventListener?.('change', () => this.syncHeroVideos(), listenerOptions);

      this.refresh();
      if (!namespace.pageViewEmitted) {
        namespace.pageViewEmitted = true;
        emit('landing_page_view');
      }
    }

    scheduleRefresh() {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.refresh(), 0);
    }

    refresh() {
      this.disconnectObservers();
      this.hero = document.querySelector('[data-lp-hero]');
      this.heroVideos = Array.from(document.querySelectorAll('[data-lp-hero-video]'));
      this.commerce = document.querySelector('[data-lp-commerce]');
      this.sticky = document.querySelector('[data-lp-sticky]');
      this.mainForm = document.querySelector('[data-lp-product-form]');
      this.footer = document.querySelector('.shopify-section-group-footer-group, #shopify-section-footer, body > footer');

      document.querySelectorAll('[data-lp-comparison]').forEach((comparison) => {
        const range = comparison.querySelector('[data-lp-comparison-range]');
        if (range) comparison.style.setProperty('--ln-pillow-compare-pos', `${range.value}%`);
      });

      this.syncHeroVideos();
      this.initObservers();
      this.updatePurchaseState('default');
      this.updateStickyVisibility();
    }

    syncHeroVideos() {
      this.heroVideos?.forEach((video) => {
        if (motionPreference.matches) {
          video.pause();
          video.classList.remove('is-ready');
          return;
        }

        const revealVideo = () => video.classList.add('is-ready');
        if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
          revealVideo();
        } else if (video.dataset.lpReadyBound !== 'true') {
          video.dataset.lpReadyBound = 'true';
          video.addEventListener('canplay', revealVideo, { once: true });
        }

        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      });
    }

    disconnectObservers() {
      this.reachObserver?.disconnect();
      this.heroObserver?.disconnect();
      this.commerceObserver?.disconnect();
      this.footerObserver?.disconnect();
    }

    initObservers() {
      if (!('IntersectionObserver' in window)) {
        this.heroExited = this.hero ? this.hero.getBoundingClientRect().bottom <= 0 : false;
        this.commerceReached = this.commerce ? this.commerce.getBoundingClientRect().top < window.innerHeight : false;
        this.updateStickyVisibility();
        return;
      }

      if (this.hero) {
        this.heroObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          this.heroExited = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          this.updateStickyVisibility();
        }, { threshold: 0 });
        this.heroObserver.observe(this.hero);
      }

      if (this.commerce) {
        this.commerceObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.commerceReached = true;
          this.updatePurchaseState('default');
        }, { threshold: .15, rootMargin: '0px 0px -28% 0px' });
        this.commerceObserver.observe(this.commerce);
      }

      if (this.footer && this.sticky) {
        this.footerObserver = new IntersectionObserver((entries) => {
          this.sticky?.classList.toggle('is-footer-visible', entries.some((entry) => entry.isIntersecting));
        }, { threshold: 0 });
        this.footerObserver.observe(this.footer);
      }

      const reachTargets = Array.from(document.querySelectorAll('[data-lp-reach-event]'));
      if (reachTargets.length) {
        this.reachObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || this.reachedSections.has(entry.target)) return;
            this.reachedSections.add(entry.target);
            emit(entry.target.dataset.lpReachEvent, {
              section: entry.target.dataset.lpSection || ''
            });
            this.reachObserver?.unobserve(entry.target);
          });
        }, { threshold: .2, rootMargin: '0px 0px -12% 0px' });
        reachTargets.forEach((target) => this.reachObserver.observe(target));
      }
    }

    onClick(event) {
      const jump = event.target.closest?.('[data-lp-jump-commerce]');
      if (jump) {
        event.preventDefault();
        const eventName = jump.dataset.lpEvent;
        if (eventName) emit(eventName, { cta: jump.dataset.lpCta || '' });
        this.scrollToCommerce(jump.dataset.lpFocusSelector === 'true');
        return;
      }

      const swatch = event.target.closest?.('[data-lp-color-swatch]');
      if (swatch && !swatch.disabled) {
        this.selectSwatch(swatch);
        return;
      }

      const stickyButton = event.target.closest?.('[data-lp-sticky-button]');
      if (stickyButton) {
        event.preventDefault();
        emit('sticky_cta_click', {
          selectedVariantId: this.selectedSwatch()?.dataset.variantId || null
        });
        if (this.commerceReached && this.selectedSwatch()) this.addSelectedVariant(stickyButton);
        else this.scrollToCommerce(true);
      }
    }

    onInput(event) {
      const range = event.target.closest?.('[data-lp-comparison-range]');
      if (!range) return;
      const comparison = range.closest('[data-lp-comparison]');
      comparison?.style.setProperty('--ln-pillow-compare-pos', `${range.value}%`);
      this.markComparisonInteraction(range);
    }

    onKeydown(event) {
      const range = event.target.closest?.('[data-lp-comparison-range]');
      if (!range || !['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp'].includes(event.key)) return;
      event.preventDefault();

      const minimum = Number(range.min || 0);
      const maximum = Number(range.max || 100);
      const step = Number(range.step || 1);
      const current = Number(range.value || 0);
      let next = current;

      if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = current - step;
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = current + step;
      if (event.key === 'PageDown') next = current - (step * 10);
      if (event.key === 'PageUp') next = current + (step * 10);
      if (event.key === 'Home') next = minimum;
      if (event.key === 'End') next = maximum;

      range.value = String(Math.min(maximum, Math.max(minimum, next)));
      range.closest('[data-lp-comparison]')?.style.setProperty('--ln-pillow-compare-pos', `${range.value}%`);
      this.markComparisonInteraction(range);
    }

    markComparisonInteraction(range) {
      if (range.dataset.lpInteracted !== 'true' && Number(range.value) !== 50) {
        range.dataset.lpInteracted = 'true';
        emit('controlled_comparison_interaction', { position: Number(range.value) });
      }
    }

    onSubmit(event) {
      const form = event.target.closest?.('[data-lp-product-form]');
      if (!form) return;
      event.preventDefault();
      this.addSelectedVariant(event.submitter || form.querySelector('[data-lp-main-atc]'));
    }

    onToggle(event) {
      const details = event.target.closest?.('[data-lp-faq-item]');
      if (!details) return;
      const summary = details.querySelector('summary');
      summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      if (details.open) {
        emit('faq_open', {
          question: details.dataset.lpFaqQuestion || summary?.textContent?.trim() || ''
        });
      }
    }

    selectedSwatch() {
      return this.commerce?.querySelector('[data-lp-color-swatch][aria-pressed="true"]') || null;
    }

    selectSwatch(swatch) {
      const commerce = swatch.closest('[data-lp-commerce]');
      if (!commerce) return;

      commerce.querySelectorAll('[data-lp-color-swatch]').forEach((button) => {
        button.setAttribute('aria-pressed', button === swatch ? 'true' : 'false');
      });

      const variantInput = commerce.querySelector('[data-lp-variant-input]');
      const selectedLabel = commerce.querySelector('[data-lp-selected-color]');
      if (variantInput) variantInput.value = swatch.dataset.variantId || '';
      if (selectedLabel) selectedLabel.textContent = swatch.dataset.color || '';

      const image = commerce.querySelector('[data-lp-commerce-image], .ln-pillow-commerce__image');
      if (image && swatch.dataset.mediaSrc) {
        image.src = swatch.dataset.mediaSrc;
        if (swatch.dataset.mediaSrcset) image.srcset = swatch.dataset.mediaSrcset;
        else image.removeAttribute('srcset');
        image.alt = swatch.dataset.mediaAlt || image.alt;
      }

      emit('color_selected', {
        color: swatch.dataset.color || '',
        productId: swatch.dataset.productId || '',
        variantId: swatch.dataset.variantId || '',
        price: Number(swatch.dataset.priceCents || 0)
      });
      this.updatePurchaseState('default');
    }

    scrollToCommerce(focusSelector = false) {
      this.commerce?.scrollIntoView({ behavior: motionBehavior(), block: 'start' });
      if (focusSelector) {
        window.setTimeout(() => {
          this.commerce?.querySelector('[data-lp-color-swatch]:not(:disabled), [data-lp-b-swatch]:not(:disabled)')?.focus({ preventScroll: true });
        }, motionBehavior() === 'smooth' ? 450 : 0);
      }
    }

    currentLabels() {
      const root = this.commerce;
      return {
        choose: root?.dataset.chooseLabel || 'Choose a Color',
        add: root?.dataset.addLabel || 'Add to Cart',
        adding: root?.dataset.addingLabel || 'Adding…',
        added: root?.dataset.addedLabel || 'Added',
        error: root?.dataset.errorLabel || 'Couldn’t add — try again'
      };
    }

    updatePurchaseState(state, message = '') {
      if (!this.commerce) return;
      const swatch = this.selectedSwatch();
      const price = swatch?.dataset.priceLabel || this.commerce.dataset.defaultPrice || '';
      const labels = this.currentLabels();
      const mainButton = this.commerce.querySelector('[data-lp-main-atc]');
      const stickyButton = this.sticky?.querySelector('[data-lp-sticky-button]');
      const status = this.commerce.querySelector('[data-lp-cart-status]');

      let mainLabel = labels.choose;
      let stickyLabel = labels.choose.replace(/^Choose a /i, 'Choose ');
      let mainDisabled = !swatch;
      let stickyDisabled = false;

      if (swatch) {
        mainLabel = `${labels.add} — ${price}`;
        stickyLabel = this.commerceReached ? `${labels.add} — ${price}` : labels.choose.replace(/^Choose a /i, 'Choose ');
      }
      if (state === 'loading') {
        mainLabel = labels.adding;
        stickyLabel = labels.adding;
        mainDisabled = true;
        stickyDisabled = true;
      } else if (state === 'added') {
        mainLabel = labels.added;
        stickyLabel = labels.added;
        mainDisabled = true;
        stickyDisabled = true;
      } else if (state === 'error') {
        mainLabel = labels.error;
        stickyLabel = labels.error;
      }

      if (mainButton) {
        mainButton.disabled = mainDisabled;
        mainButton.textContent = mainLabel;
        mainButton.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
      }
      if (stickyButton) {
        stickyButton.disabled = stickyDisabled;
        stickyButton.textContent = stickyLabel;
        stickyButton.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
      }
      if (status) {
        status.textContent = state === 'error' ? message || labels.error : '';
      }
    }

    updateStickyVisibility() {
      if (!this.sticky) return;
      this.sticky.classList.toggle('is-visible', this.heroExited);
      this.updatePurchaseState('default');
    }

    async updateCartCount() {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
        bubble.classList.toggle('hidden', cart.item_count === 0);
        const visible = bubble.querySelector('[aria-hidden="true"]');
        const hidden = bubble.querySelector('.visually-hidden');
        if (visible) visible.textContent = cart.item_count;
        if (hidden) hidden.textContent = `${cart.item_count} items`;
      });
    }

    async addSelectedVariant(trigger) {
      const swatch = this.selectedSwatch();
      if (!swatch || this.cartLocked || swatch.disabled) {
        if (!swatch) this.scrollToCommerce(true);
        return;
      }

      const variantId = Number(swatch.dataset.variantId || 0);
      if (!variantId) return;
      this.cartLocked = true;
      this.updatePurchaseState('loading');

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const cartNotification = document.querySelector('cart-notification');
        const sections = [];

        if (typeof cartDrawer?.getSectionsToRender === 'function') {
          cartDrawer.getSectionsToRender().forEach((section) => sections.push(section.id));
        } else if (typeof cartNotification?.getSectionsToRender === 'function') {
          cartNotification.getSectionsToRender().forEach((section) => sections.push(section.id));
        } else {
          sections.push('cart-drawer', 'cart-icon-bubble');
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: [...new Set(sections)].join(','),
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || this.currentLabels().error);

        if (typeof cartDrawer?.renderContents === 'function') {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.setActiveElement?.(trigger);
          cartDrawer.renderContents(data);
        } else if (typeof cartNotification?.renderContents === 'function') {
          cartNotification.setActiveElement?.(trigger);
          cartNotification.renderContents(data);
        } else {
          await this.updateCartCount().catch(() => {});
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data }));
        }

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        emit('add_to_cart', {
          productId: swatch.dataset.productId || '',
          variantId: String(variantId),
          color: swatch.dataset.color || '',
          price: Number(swatch.dataset.priceCents || 0)
        });
        this.updatePurchaseState('added');
        window.setTimeout(() => this.updatePurchaseState('default'), 1600);
      } catch (error) {
        this.updatePurchaseState('error', error.message || this.currentLabels().error);
        window.setTimeout(() => this.updatePurchaseState('default'), 2400);
      } finally {
        this.cartLocked = false;
      }
    }

    destroy() {
      window.clearTimeout(this.refreshTimer);
      this.disconnectObservers();
      this.abortController.abort();
    }
  }

  const boot = () => {
    if (!document.querySelector('[data-lp-version="A-v1.2"]')) return;
    if (namespace.controller) {
      namespace.controller.refresh();
      return;
    }
    namespace.controller = new PillowStoryController();
    namespace.emit = emit;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
